import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';

interface DateRangeFilterProps {
  value: string;
  onChange: (value: string) => void;
  customStartDate?: string;
  customEndDate?: string;
  onCustomDateChange?: (startDate: string, endDate: string) => void;
}

export default function DateRangeFilter({ 
  value, 
  onChange, 
  customStartDate, 
  customEndDate, 
  onCustomDateChange 
}: DateRangeFilterProps) {
  const isDarkMode = useTheme((state) => state.theme);
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);

  const dateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const selectedOption = dateOptions.find(option => option.value === value);

  const handleOptionSelect = (optionValue: string) => {
    if (optionValue === 'custom') {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate && onCustomDateChange) {
      onCustomDateChange(customStartDate, customEndDate);
      onChange('custom');
    }
    setShowCustomRange(false);
  };

  return (
    <div className="relative">
      {/* Main Filter Button */}
      <div className="flex items-center gap-2">
        <Calendar className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`px-3 py-2 rounded-lg cursor-pointer focus:ring-2 focus:ring-[#1a9f6b] outline-none transition-all flex items-center gap-2 ${
              isDarkMode
                ? 'bg-[#050a0a] text-white hover:bg-[#0f1919] border border-[#1a2424]'
                : 'bg-gray-100 text-[#053333] hover:bg-gray-200 border border-gray-300'
            }`}
          >
            <span>{selectedOption?.label || 'Select Date'}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className={`absolute top-full left-0 mt-1 w-48 rounded-lg shadow-lg border z-10 ${
              isDarkMode
                ? 'bg-[#0a1414] border-[#1a2424]'
                : 'bg-white border-gray-200'
            }`}>
              {dateOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                  className={`w-full px-3 py-2 text-left hover:bg-opacity-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    value === option.value
                      ? (isDarkMode ? 'bg-[#1a9f6b]/20 text-[#1a9f6b]' : 'bg-[#1a9f6b]/10 text-[#1a9f6b]')
                      : (isDarkMode ? 'text-white hover:bg-[#050a0a]' : 'text-[#053333] hover:bg-gray-100')
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Custom Date Range Modal */}
      {showCustomRange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 w-96 shadow-xl ${
            isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
              Select Custom Date Range
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  From Date
                </label>
                <input
                  type="date"
                  value={customStartDate || ''}
                  onChange={(e) => onCustomDateChange?.(e.target.value, customEndDate || '')}
                  className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-[#1a9f6b] outline-none ${
                    isDarkMode
                      ? 'bg-[#050a0a] text-white border-[#1a2424]'
                      : 'bg-white text-[#053333] border-gray-300'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  To Date
                </label>
                <input
                  type="date"
                  value={customEndDate || ''}
                  onChange={(e) => onCustomDateChange?.(customStartDate || '', e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-[#1a9f6b] outline-none ${
                    isDarkMode
                      ? 'bg-[#050a0a] text-white border-[#1a2424]'
                      : 'bg-white text-[#053333] border-gray-300'
                  }`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  !customStartDate || !customEndDate
                    ? (isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400')
                    : 'bg-[#1a9f6b] text-white hover:bg-[#148856]'
                }`}
              >
                Apply
              </button>
              <button
                onClick={() => setShowCustomRange(false)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-[#053333] hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}